import React, { useRef, useEffect, useState } from "react";
import { PencilIcon, Copy } from "lucide-react";
import { Popover, PopoverButton, PopoverPanel } from "@headlessui/react";
import { useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import LayoutAction from "../../../actions/LayoutAction";

type ActionType = "edit" | "copy" | "show-all";

interface EditableDetailRowProps {
  label: string;
  children: React.ReactNode;
  editComponent: React.ReactNode;
  onSave?: () => void;
  /** When true, row switches to edit mode immediately (and focuses input) */
  autoEdit?: boolean;
  /** When true, uses Popover mode (overlay). When false or undefined, uses inline editing */
  usePopover?: boolean;
  /** Icon to display next to the value. Overrides default icons */
  icon?: React.ReactNode;
  /** Array of actions to display as icons. Defaults to ["edit"] */
  actions?: ActionType[];
  rightComponent?: React.ReactNode;
}

interface PopoverContentProps {
  children: React.ReactNode;
  editNode: React.ReactNode;
  popoverButtonRef: React.RefObject<HTMLElement>;
  valueContentRef: React.RefObject<HTMLDivElement>;
  onSave?: () => void;
  icon?: React.ReactNode;
  actions?: ActionType[];
}

interface PopoverContentInnerProps extends PopoverContentProps {
  open: boolean;
  wasOpenRef: React.MutableRefObject<boolean>;
  focusDialogInput: () => void;
  updatePanelPosition: (valueContent: HTMLDivElement, button: HTMLElement) => void;
  handleActionClick: (action: ActionType, e: React.MouseEvent) => void;
}

// Constants
const DEFAULT_ACTIONS: ActionType[] = ["edit"];
const DEFAULT_ICON_CLASS = "w-3 h-3 text-gray-400 mr-2 text-[10px]";
const FOCUSABLE_SELECTOR = 'input, textarea, [contenteditable="true"], [contenteditable=""]';
const DIALOG_FOCUSABLE_SELECTOR =
  '[role="dialog"] input, [role="dialog"] textarea, [role="dialog"] [contenteditable="true"], [role="dialog"] [contenteditable=""]';
const SELECT_INLINE_SELECTORS = [".select-inline__menu", ".select-inline__option"];

// Helper functions
const isInsideSelectInline = (element: HTMLElement | null): boolean => {
  if (!element) return false;
  return SELECT_INLINE_SELECTORS.some((selector) => element.closest?.(selector));
};

const findInputElement = (
  container: HTMLElement | null
): HTMLInputElement | HTMLTextAreaElement | null => {
  if (!container) return null;
  return container.querySelector<HTMLInputElement | HTMLTextAreaElement>("input, textarea");
};

const waitForNextFrames = (callback: () => void) => {
  requestAnimationFrame(() => {
    requestAnimationFrame(callback);
  });
};

const blurInputAndWait = (inputElement: HTMLInputElement | HTMLTextAreaElement): Promise<void> => {
  return new Promise<void>((resolve) => {
    const handleBlur = () => {
      inputElement.removeEventListener("blur", handleBlur);
      waitForNextFrames(resolve);
    };
    inputElement.addEventListener("blur", handleBlur, { once: true });
    inputElement.blur();
  });
};

const closeEditMode = (onSave?: () => void, setIsEditing?: (value: boolean) => void) => {
  onSave?.();
  setIsEditing?.(false);
};

const extractLinkValue = (href: string): string | null => {
  if (href.startsWith("mailto:")) return href.replace("mailto:", "");
  if (href.startsWith("tel:")) return href.replace("tel:", "");
  return null;
};

const extractTextFromElement = (element: any): string => {
  if (typeof element === "string" || typeof element === "number") {
    return String(element);
  }
  if (!element) return "";

  if (React.isValidElement(element)) {
    const props = (element.props as any) || {};
    if (props.href && typeof props.href === "string") {
      const linkValue = extractLinkValue(props.href);
      if (linkValue) return linkValue;
      // For regular URLs (e.g., social media links), copy the full href
      return props.href;
    }
    if (props.children) return extractTextFromElement(props.children);
  }

  if (Array.isArray(element)) {
    return element.map(extractTextFromElement).join("");
  }

  return "";
};

const renderActionIcon = (action: ActionType, className: string = DEFAULT_ICON_CLASS) => {
  switch (action) {
    case "copy":
      return <Copy className={className} />;
    case "show-all":
      return <span className={className}>Show all</span>;
    case "edit":
    default:
      return <PencilIcon className={className} />;
  }
};

const renderIconGroup = (
  actions: ActionType[],
  onActionClick: (action: ActionType, e: React.MouseEvent) => void,
  icon?: React.ReactNode
) => {
  if (icon) {
    return (
      <div onClick={(e) => onActionClick("edit", e)} className="cursor-pointer">
        {icon}
      </div>
    );
  }

  const actionsToRender = actions.length === 0 ? DEFAULT_ACTIONS : actions;

  return (
    <div className="flex items-center gap-1">
      {actionsToRender.map((action) => (
        <div
          key={action}
          onClick={(e) => onActionClick(action, e)}
          className="cursor-pointer hover:text-gray-600 transition-colors"
        >
          {renderActionIcon(action)}
        </div>
      ))}
    </div>
  );
};

const copyToClipboard = async (
  text: string,
  dispatch: any,
  t: (key: string) => string
): Promise<void> => {
  try {
    await navigator.clipboard.writeText(text);
    dispatch(
      LayoutAction.setNotification({
        title: t("Info"),
        type: "success",
        message: t("Copied!"),
      })
    );
  } catch (err) {
    console.error("Failed to copy:", err);
    dispatch(
      LayoutAction.setNotification({
        title: t("Error"),
        type: "error",
        message: t("Failed to copy"),
      })
    );
  }
};

const handleCopyAction = async (
  children: React.ReactNode,
  dispatch: any,
  t: (key: string) => string
) => {
  const textToCopy = extractTextFromElement(children);
  if (textToCopy) {
    await copyToClipboard(textToCopy, dispatch, t);
  }
};

const handleEditAction = (popoverButtonRef: React.RefObject<HTMLElement>) => {
  if (popoverButtonRef.current) {
    popoverButtonRef.current.click();
  }
};

const PopoverContentInner: React.FC<PopoverContentInnerProps> = ({
  open,
  children,
  editNode,
  popoverButtonRef,
  valueContentRef,
  onSave,
  icon,
  actions = DEFAULT_ACTIONS,
  wasOpenRef,
  focusDialogInput,
  updatePanelPosition,
  handleActionClick,
}) => {
  useEffect(() => {
    if (!open || !valueContentRef.current || !popoverButtonRef.current) {
      return;
    }

    const valueContent = valueContentRef.current;
    const button = popoverButtonRef.current;

    const performUpdate = () => updatePanelPosition(valueContent, button);

    performUpdate();
    waitForNextFrames(performUpdate);

    const handleUpdate = () => performUpdate();
    window.addEventListener("scroll", handleUpdate, true);
    window.addEventListener("resize", handleUpdate);

    return () => {
      window.removeEventListener("scroll", handleUpdate, true);
      window.removeEventListener("resize", handleUpdate);
    };
  }, [open, valueContentRef, popoverButtonRef, updatePanelPosition]);

  useEffect(() => {
    if (open && !wasOpenRef.current) {
      focusDialogInput();
    }

    if (!open && wasOpenRef.current) {
      onSave?.();
    }

    wasOpenRef.current = open;
  }, [open, onSave, focusDialogInput, wasOpenRef]);

  return (
    <>
      <PopoverButton
        as="div"
        ref={popoverButtonRef as any}
        className="w-full cursor-pointer flex items-center"
      >
        <div className="flex items-center justify-between w-full rounded-md transition-colors duration-150 hover:bg-gray-50 flex-1 min-h-[28px]">
          <span className="truncate">{children}</span>
          <div className="ml-2 opacity-0 group-hover:opacity-100 transition-opacity shrink-0">
            {renderIconGroup(actions, handleActionClick, icon)}
          </div>
        </div>
      </PopoverButton>

      <PopoverPanel
        anchor={{
          to: "bottom start",
          gap: 0,
          offset: -28,
        }}
        className={`bg-white rounded-lg border p-4 shadow-lg z-[1000] transition-colors ${
          open ? "border-blue-500" : "border-gray-200"
        }`}
      >
        <div className="w-full">{editNode}</div>
      </PopoverPanel>
    </>
  );
};

const PopoverContent: React.FC<PopoverContentProps> = ({
  children,
  editNode,
  popoverButtonRef,
  valueContentRef,
  onSave,
  icon,
  actions = DEFAULT_ACTIONS,
}) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const wasOpenRef = useRef<boolean>(false);

  const handleActionClick = async (action: ActionType, e: React.MouseEvent) => {
    e.stopPropagation();

    if (action === "copy") {
      await handleCopyAction(children, dispatch, t);
    } else if (action === "edit" || action === "show-all") {
      handleEditAction(popoverButtonRef);
    }
  };

  const updatePanelPosition = (valueContent: HTMLDivElement, _button: HTMLElement) => {
    const width = valueContent.offsetWidth;
    const rect = valueContent.getBoundingClientRect();
    const panel = document.querySelector<HTMLElement>(
      '[data-headlessui-state="open"][data-anchor]'
    );

    if (panel && width) {
      panel.style.width = `${width}px`;
      panel.style.minWidth = `${width}px`;
      panel.style.maxWidth = `${width}px`;
      panel.style.top = `${rect.top}px`;
      panel.style.left = `${rect.left}px`;
      panel.style.position = "fixed";
    }
  };

  const focusDialogInput = () => {
    waitForNextFrames(() => {
      const focusable = document.querySelector<HTMLElement>(DIALOG_FOCUSABLE_SELECTOR);
      focusable?.focus();
    });
  };

  return (
    <Popover className="relative w-full">
      {({ open }) => (
        <PopoverContentInner
          open={open}
          children={children}
          editNode={editNode}
          popoverButtonRef={popoverButtonRef}
          valueContentRef={valueContentRef}
          onSave={onSave}
          icon={icon}
          actions={actions}
          wasOpenRef={wasOpenRef}
          focusDialogInput={focusDialogInput}
          updatePanelPosition={updatePanelPosition}
          handleActionClick={handleActionClick}
        />
      )}
    </Popover>
  );
};

const EditableDetailRow: React.FC<EditableDetailRowProps> = ({
  label,
  children,
  editComponent,
  onSave,
  autoEdit = false,
  usePopover = false,
  icon,
  actions = DEFAULT_ACTIONS,
  rightComponent,
}) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();

  // Refs
  const valueContentRef = useRef<HTMLDivElement>(null);
  const popoverButtonRef = useRef<HTMLElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);

  // State
  const [isEditing, setIsEditing] = useState<boolean>(autoEdit);

  // Handlers
  const handleActionClick = async (action: ActionType, e?: React.MouseEvent) => {
    e?.stopPropagation();

    if (action === "copy") {
      await handleCopyAction(children, dispatch, t);
    } else {
      setIsEditing(true);
    }
  };

  const handleValueClick = (e: React.MouseEvent) => {
    const target = e.target as HTMLElement;
    const linkElement = target.closest("a");

    if (linkElement) {
      if (e.ctrlKey || e.metaKey || e.button === 1) {
        return;
      }
      e.preventDefault();
      e.stopPropagation();
    }

    setIsEditing(true);
  };

  // Sync edit mode from external autoEdit prop
  useEffect(() => {
    if (!autoEdit) return;

    if (usePopover && popoverButtonRef.current) {
      popoverButtonRef.current.click();
    } else {
      setIsEditing(true);
    }
  }, [autoEdit, usePopover]);

  // Handle closing edit mode when clicking/focusing outside (inline editing only)
  useEffect(() => {
    if (usePopover || !isEditing) return;

    const wrapper = wrapperRef.current;
    if (!wrapper) return;

    const shouldIgnoreEvent = (target: HTMLElement | null): boolean => {
      return !target || wrapper.contains(target) || isInsideSelectInline(target);
    };

    const handleFocusOut = (event: FocusEvent) => {
      const relatedTarget = event.relatedTarget as HTMLElement | null;
      if (shouldIgnoreEvent(relatedTarget)) return;

      waitForNextFrames(() => {
        closeEditMode(onSave, setIsEditing);
      });
    };

    const handleClickOutside = async (event: MouseEvent) => {
      const target = event.target as HTMLElement | null;
      if (shouldIgnoreEvent(target)) return;

      const inputElement = findInputElement(wrapper);
      if (inputElement && document.activeElement === inputElement) {
        await blurInputAndWait(inputElement);
      }

      closeEditMode(onSave, setIsEditing);
    };

    wrapper.addEventListener("focusout", handleFocusOut);
    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      wrapper.removeEventListener("focusout", handleFocusOut);
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isEditing, onSave, usePopover]);

  // Auto-focus input when entering inline edit mode
  useEffect(() => {
    if (usePopover || !isEditing || !wrapperRef.current) return;

    const focusable = wrapperRef.current.querySelector<HTMLElement>(FOCUSABLE_SELECTOR);
    focusable?.focus();
  }, [isEditing, usePopover]);

  // Prepare edit component with autoFocus prop
  const editNode = React.isValidElement(editComponent)
    ? React.cloneElement(editComponent as any, { autoFocus: true })
    : editComponent;

  const renderValueContent = () => {
    if (usePopover) {
      return (
        <div className="w-full flex-1 min-w-0">
          <PopoverContent
            popoverButtonRef={popoverButtonRef}
            valueContentRef={valueContentRef}
            onSave={onSave}
            editNode={editNode}
            icon={icon}
            actions={actions}
          >
            {children}
          </PopoverContent>
        </div>
      );
    }

    if (isEditing) {
      return <div className="w-full flex items-center h-full">{editNode}</div>;
    }

    return (
      <div
        className="flex items-center justify-between w-full rounded-md transition-colors duration-150 hover:bg-gray-50 flex-1 min-h-[28px] cursor-pointer"
        onClick={handleValueClick}
        role="button"
      >
        <span className="truncate">{children}</span>
        <div className="ml-2 opacity-0 group-hover:opacity-100 transition-opacity">
          {renderIconGroup(actions, handleActionClick, icon)}
        </div>
      </div>
    );
  };

  return (
    <div className="flex items-center w-full py-0.5" ref={wrapperRef}>
      <div className="w-[150px] text-[12px] leading-4 font-medium text-gray-500 ml-1 py-1.5">
        {label}
      </div>
      <div
        ref={valueContentRef}
        className="text-[12px] leading-4 font-medium text-gray-800 truncate min-w-0 flex-1 w-full relative group flex items-center"
      >
        <div className="w-full flex items-center gap-1">
          {renderValueContent()}
          <div className="shrink-0">{rightComponent}</div>
        </div>
      </div>
    </div>
  );
};

export default EditableDetailRow;
